export type IndustryKey = "mortgage" | "dentist" | "trades" | "beauty" | "professional" | "restaurant";

export interface IndustryTemplateSeed {
  key: IndustryKey;
  name: string;
  description: string;
  defaultServices: string[];
  defaultFields: string[];
  flowGoals: string[];
  promptTone: string;
}

export const industryTemplates: IndustryTemplateSeed[] = [
  {
    key: "mortgage",
    name: "Mortgage adviser appointment desk",
    description:
      "Book adviser calls, capture non-sensitive callback notes, and send advice or calculation requests to a licensed adviser.",
    defaultServices: ["Mortgage adviser callback", "Refinance appointment", "First-home buyer call", "Insurance adviser call", "Document preparation call"],
    defaultFields: ["appointment_reason", "product_area", "preferred_callback_window", "contact_method", "consent_to_follow_up"],
    flowGoals: ["Book adviser callbacks", "Capture appointment-ready notes", "Avoid advice and calculations"],
    promptTone: "Professional, low-pressure, and compliant. Stay appointment-only and send mortgage specifics to a licensed adviser.",
  },
  {
    key: "dentist",
    name: "Clinics, dental, physio & vet",
    description:
      "Handle high-volume bookings, changes, cancellations, FAQs, and urgent care requests without giving clinical advice.",
    defaultServices: ["New patient booking", "Appointment change", "Urgent care triage", "Repeat visit", "General FAQ"],
    defaultFields: ["care_need", "urgency", "preferred_practitioner", "existing_patient_or_pet", "privacy_safe_contact"],
    flowGoals: ["Book or change appointments", "Escalate urgent symptoms", "Protect privacy and avoid diagnosis"],
    promptTone: "Calm, clear, clinical, and reassuring. Avoid diagnosis; book care or ask staff to step in.",
  },
  {
    key: "trades",
    name: "Trades & home services",
    description:
      "Capture appointment requests, site details, urgency, and after-hours messages for missed-call-heavy service teams.",
    defaultServices: ["Job callback", "Site visit booking", "Urgent repair appointment", "After-hours message", "Maintenance booking"],
    defaultFields: ["job_type", "site_address", "urgency", "access_notes", "photo_or_callback_needed"],
    flowGoals: ["Book site visits", "Send urgent work to the team", "Prepare clear appointment notes"],
    promptTone: "Direct, practical, and local. Gather job facts, location, urgency, and contact details before asking the team to step in.",
  },
  {
    key: "beauty",
    name: "Beauty, salon & wellness",
    description: "Match services, staff, rooms, durations, deposit expectations, patch-test rules, and simple reschedules.",
    defaultServices: ["Hair colour", "Facial consult", "Wellness appointment", "Lash appointment", "Reschedule or cancellation"],
    defaultFields: ["service_goal", "preferred_staff", "deposit_consent", "patch_test_status", "preferred_time"],
    flowGoals: ["Book services by duration", "Explain deposit rules", "Handle reschedules"],
    promptTone: "Warm, polished, premium, and concise. Confirm service details before suggesting a slot.",
  },
  {
    key: "professional",
    name: "Legal, accounting & property management",
    description:
      "Sort appointment requests, book callbacks, collect document-prep notes, and send advice questions to a qualified person.",
    defaultServices: ["New enquiry callback", "Appointment request", "Document preparation call", "Existing client message", "Urgent matter review"],
    defaultFields: ["matter_type", "deadline", "client_status", "documents_available", "callback_window"],
    flowGoals: ["Book the right callback", "Capture document-prep needs", "Avoid professional advice"],
    promptTone: "Clear, careful, and structured. Understand the matter and send advice questions to a qualified person without giving advice.",
  },
  {
    key: "restaurant",
    name: "Restaurant",
    description: "Take bookings, manage party size rules, special requests, waitlists, and large-table requests.",
    defaultServices: ["Dinner table", "Large group", "Private room", "Waitlist"],
    defaultFields: ["party_size", "preferred_time", "dietary_notes", "occasion"],
    flowGoals: ["Confirm table slots", "Escalate large tables", "Capture special requests"],
    promptTone: "Fast, hospitable, and practical. Keep booking confirmation short and repeat key details.",
  },
];
